/*!
 * Copyright (c) Microsoft Corporation. All rights reserved.
 */

import React, { useEffect } from "react";

import { useDispatch, useSelector } from "react-redux";

import {
  DetailsList,
  DetailsListLayoutMode,
  IColumn,
  PrimaryButton,
  SelectionMode,
  Stack,
} from "@fluentui/react";

import * as ClRegionActions from "Dataflows/ClRegion/ClRegionAction";
import * as DemographicsActions from "Dataflows/Demographics/DemographicsAction";
import * as DemographicsSelectors from "Dataflows/Demographics/DemographicsSelector";
import * as TechnologyAreaActions from "Dataflows/TechnologyArea/TechnologyAreaAction";

import { Title } from "Components/Common/Results/Result.styles";
import { detailListStyles, filterButtonStyles } from "Components/Common/SharedListStyles.styles";
import { DemographicsBarChart } from "Components/Demographics/DemographicsBarChart/DemographicsBarChart";
import { DemographicsPieChart } from "Components/Demographics/DemographicsPieChart/DemographicsPieChart";
import {
  defaultMvpDemographicsFilterValues,
  mvpApplicationDemographicsFilterFields,
  ApplicationTypeEnum,
  APPLICATION_TYPE,
} from "Constants/ApplicationConstants";
import { TenantTypesEnum } from "Constants/TenantTypes";
import {
  buttonStackTokens,
  containerStackStyles,
  listStackStyles,
  tableStackStyles,
  PaddedFlexRow,
} from "Containers/Common/CommonContainer.styles";
import SideBarContainer from "Containers/SideBarContainer/SideBarContainer";
import { ICapDataRow } from "Interfaces/Demographics/ICapDataRow";
import { IFilterValue } from "Interfaces/IFilterValue";

import {
  cardStyle,
  cardTextStyle,
  demographicsStackItemStyle,
  splitStackStyle,
  InfoText,
  Line,
} from "./DemographicsController.styles";

const DemographicsContainer: React.FunctionComponent = () => {
  const dispatch = useDispatch();
  const filterDemographics = (
    selectedDemographicsFilterValues: IFilterValue[],
    applicationType: string,
    tenant: TenantTypesEnum
  ) =>
    dispatch(
      DemographicsActions.filterDemographics(
        selectedDemographicsFilterValues,
        applicationType,
        tenant
      )
    );

  const loadCapData = () => dispatch(DemographicsActions.capDataLoad());

  const addDemographicsFilterValue = (filterValue: IFilterValue) =>
    dispatch(DemographicsActions.addDemographicsFilterValue(filterValue));
  const removeDemographicsFilterValue = (filterValue: IFilterValue) =>
    dispatch(DemographicsActions.removeDemographicsFilterValue(filterValue));
  const updateDemographicsFilterValue = (filterValue: IFilterValue) =>
    dispatch(DemographicsActions.updateDemographicsFilterValue(filterValue));

  const loadCLRegions = () => dispatch(ClRegionActions.getClRegions());
  const loadTechnologyAreas = () =>
    dispatch(TechnologyAreaActions.getTechnologyAreas(TenantTypesEnum.MVP));

  const selectedDemographicsFilterValues = useSelector(
    DemographicsSelectors.getDemographicsFilterValues
  );
  const filteredDemographics = useSelector(DemographicsSelectors.getDemographics);
  const capData = useSelector(DemographicsSelectors.getCapData);

  useEffect(() => {
    loadCLRegions();
    loadTechnologyAreas();
    filterDemographics([], ApplicationTypeEnum.ANY, TenantTypesEnum.MVP);
    loadCapData();
  }, []);

  const handleFilter = (selectedDemographicsFilterValues: IFilterValue[]): void => {
    filterDemographics(
      selectedDemographicsFilterValues,
      selectedDemographicsFilterValues.find((v) => v.fieldName === APPLICATION_TYPE)?.value ??
        ApplicationTypeEnum.ANY,
      TenantTypesEnum.MVP
    );
  };

  /**
   * Call to reset filter values.
   * @method
   */
  const resetFilterValues = (): void => {
    dispatch(DemographicsActions.resetDemographics());

    handleFilter(defaultMvpDemographicsFilterValues);
  };

  const renderProjectedPopulationColumn = (item: ICapDataRow): React.ReactElement => (
    <>{item.incomingCohort + item.activeUsers}</>
  );
  const renderCapacityColumn = (item: ICapDataRow): React.ReactElement => {
    const isOverCapacity = item.incomingCohort + item.activeUsers >= item.capacity;
    const capStyle: React.CSSProperties = {
      background: isOverCapacity ? "yellow" : undefined,
      fontWeight: isOverCapacity ? "bold" : undefined,
      textAlign: "center",
    };
    return <div style={capStyle}>{item.capacity}</div>;
  };

  const capTableColumns: IColumn[] = [
    {
      key: "awardCategoryName",
      name: "Award Category",
      ariaLabel: "Award Category",
      fieldName: "awardCategoryName",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "activeUsers",
      name: "Active Users",
      ariaLabel: "Active Users",
      fieldName: "activeUsers",
      minWidth: 100,
      maxWidth: 100,
      isResizable: true,
    },
    {
      key: "incomingCohort",
      name: "Incoming Cohort",
      ariaLabel: "Incoming Cohort",
      fieldName: "incomingCohort",
      minWidth: 160,
      isResizable: true,
    },
    {
      key: "projectedPopulation",
      name: "Projected Population",
      ariaLabel: "Projected Population",
      fieldName: "projectedPopulation",
      minWidth: 160,
      isResizable: true,
      onRender: (item: ICapDataRow): React.ReactElement => {
        return renderProjectedPopulationColumn(item);
      },
    },
    {
      key: "capacity",
      name: "Capacity",
      ariaLabel: "Capacity",
      fieldName: "capacity",
      minWidth: 100,
      isResizable: true,
      onRender: (item: ICapDataRow): React.ReactElement => {
        return renderCapacityColumn(item);
      },
    },
  ];

  return (
    <Stack horizontal styles={containerStackStyles}>
      <Stack.Item disableShrink>
        <SideBarContainer
          addFilterValue={addDemographicsFilterValue}
          removeFilterValue={removeDemographicsFilterValue}
          updateFilterValue={updateDemographicsFilterValue}
          selectedFilterValues={selectedDemographicsFilterValues}
          filterFields={mvpApplicationDemographicsFilterFields}
        />
        <Stack horizontal tokens={buttonStackTokens}>
          <PrimaryButton
            styles={filterButtonStyles}
            text="Apply"
            onClick={(): void => {
              handleFilter(selectedDemographicsFilterValues);
            }}
          />
          <PrimaryButton
            styles={filterButtonStyles}
            text="Reset"
            onClick={(): void => resetFilterValues()}
          />
        </Stack>
      </Stack.Item>
      <Stack>
        <Stack.Item grow styles={listStackStyles()}>
          <Stack styles={tableStackStyles}>
            <PaddedFlexRow>
              <Title>Demographics</Title>
            </PaddedFlexRow>
            {filteredDemographics && (
              <Stack>
                <Stack horizontal horizontalAlign="space-around">
                  <Stack style={splitStackStyle} verticalAlign="space-between">
                    <Stack.Item styles={cardStyle}>
                      <h5>Average Score for Cohort</h5>
                      <span style={cardTextStyle}>
                        {filteredDemographics.averageApplicationScore}
                      </span>
                    </Stack.Item>
                    <Stack.Item styles={cardStyle}>
                      <h5>Number of Applications</h5>
                      <span style={cardTextStyle}>{filteredDemographics.numberOfApplications}</span>
                    </Stack.Item>
                  </Stack>

                  <Stack.Item>
                    <DemographicsPieChart data={filteredDemographics.genderData} title="Gender" />
                  </Stack.Item>
                </Stack>

                <Stack horizontal horizontalAlign="space-around">
                  <Stack.Item style={demographicsStackItemStyle}>
                    <DemographicsPieChart
                      data={filteredDemographics.declineReasonData}
                      title="Decline Reason Data"
                    />
                  </Stack.Item>
                  <Stack.Item style={demographicsStackItemStyle}>
                    <DemographicsPieChart
                      data={filteredDemographics.statusData}
                      title="Pending Status Breakdown"
                    />
                  </Stack.Item>
                </Stack>

                <Stack.Item style={demographicsStackItemStyle}>
                  <DemographicsBarChart data={filteredDemographics.regionBarData} title="Region" />
                </Stack.Item>

                <Stack.Item style={demographicsStackItemStyle}>
                  <DemographicsBarChart
                    data={filteredDemographics.awardCategoryBarData}
                    title="Award Category"
                  />
                </Stack.Item>
              </Stack>
            )}

            <Line />

            <Stack.Item>
              <PaddedFlexRow>
                <Title>Capacity</Title>
                <InfoText>* This table is unaffected by the filters</InfoText>
              </PaddedFlexRow>
              <DetailsList
                items={capData.capacityData}
                columns={capTableColumns}
                selectionMode={SelectionMode.none}
                layoutMode={DetailsListLayoutMode.fixedColumns}
                isHeaderVisible={true}
                styles={detailListStyles}
              />
            </Stack.Item>
          </Stack>
        </Stack.Item>
      </Stack>
    </Stack>
  );
};

export default DemographicsContainer;
